import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Form, Input, Switch, Button, Upload, Tabs, Typography, message, Space, Spin, Divider
} from 'antd';
import {
  UploadOutlined, PlusOutlined, CopyOutlined, HomeOutlined, TeamOutlined, CloudServerOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

// ══════════════════════════════════════════════
//  Mock 数据
// ══════════════════════════════════════════════

// TODO: 联调时替换为接口数据
const MOCK_BRAND_CONFIG = {
  pageTitle: '',
  faviconUrl: '',
  enabled: true,
  loginLogoUrl: '',
  loginBrandName: '',
  loginBannerUrl: '',
  loginHeroTitle: '',
  loginHeroHighlight: '',
  loginHeroSubtitle: '',
  headerLogoUrl: '',
  headerTitle: '',
  brandCode: 'DEMO001',
};

// 预览用菜单数据（仅展示，不可编辑）
const PREVIEW_MENU_ITEMS = [
  { label: '首页', icon: <HomeOutlined /> },
  { label: '企业管理', icon: <TeamOutlined /> },
  { label: '物模型管理', icon: <CloudServerOutlined /> },
  { label: '产品管理', icon: <CloudServerOutlined /> },
  { label: '固件管理', icon: <CloudServerOutlined /> },
  { label: '设备中心', icon: <CloudServerOutlined /> },
];

// ══════════════════════════════════════════════
//  子组件：登录页预览
// ══════════════════════════════════════════════
const LoginPreview = ({ config }) => {
  const hasBanner = config.loginBannerUrl;
  return (
    <div style={{
      width: '100%', paddingBottom: '75%', position: 'relative', background: '#fff',
      borderRadius: 8, overflow: 'hidden', border: '1px solid #f0f0f0',
    }}>
      <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
        {/* 左侧品牌区 */}
        <div style={{
          flex: 1.2, position: 'relative', overflow: 'hidden',
          background: hasBanner ? `url(${config.loginBannerUrl}) center/cover no-repeat` :
            'radial-gradient(circle at center, #d5e1df 0%, #e5edff 37%, #fff 76%)',
        }}>
          {/* 左上 Logo */}
          <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            {config.loginLogoUrl ? (
              <img src={config.loginLogoUrl} alt="" style={{ height: 18 }} />
            ) : (
              <div style={{ width: 18, height: 18, background: '#ff6a00', color: '#fff', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 'bold' }}>HM</div>
            )}
            <Text strong style={{ fontSize: 10, color: '#333' }}>
              {config.loginBrandName || '鹤梦云生态管理平台'}
            </Text>
          </div>

          {/* 品牌内容区（无 Banner 时展示） */}
          {!hasBanner && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 16px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>
                {config.loginHeroTitle || '为行业领导者打造的'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, background: 'linear-gradient(90deg, #833ab4, #fd1d1d, #fcb045)', backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent', marginBottom: 4 }}>
                {config.loginHeroHighlight || '一站式物联网生态系统'}
              </div>
              <div style={{ fontSize: 9, color: '#666' }}>
                {config.loginHeroSubtitle || '赋能制造商、解决方案提供商和分销商'}
              </div>
              {/* 功能卡片 mini 版 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 8 }}>
                {['产品生命周期管理', '无缝设备云集成', '安全的OTA更新', '自定义APP管理', '自动收益分润', '精准营销运营'].map((name, i) => (
                  <div key={i} style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <div style={{ width: 12, height: 12, background: 'rgba(64,158,255,0.12)', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
                    <Text style={{ fontSize: 7, color: '#777' }}>{name}</Text>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 右侧登录区 */}
        <div style={{ flex: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div style={{ width: '80%' }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>欢迎回来！</div>
            <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, marginBottom: 5 }} />
            <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, marginBottom: 8 }} />
            <div style={{ height: 10, background: '#ff6a00', borderRadius: 3 }} />
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ width: '40%', height: 4, background: '#f0f0f0', borderRadius: 2 }} />
              <div style={{ width: '30%', height: 4, background: '#f0f0f0', borderRadius: 2 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════
//  子组件：主界面预览
// ══════════════════════════════════════════════
const MainPreview = ({ config }) => (
  <div style={{
    width: '100%', paddingBottom: '56.25%', position: 'relative',
    borderRadius: 8, overflow: 'hidden', border: '1px solid #f0f0f0', background: '#f5f5f5',
  }}>
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ height: 14, background: '#fff', display: 'flex', alignItems: 'center', padding: '0 8px', borderBottom: '1px solid #f0f0f0', gap: 6 }}>
        {config.headerLogoUrl ? (
          <img src={config.headerLogoUrl} alt="" style={{ height: 10 }} />
        ) : (
          <div style={{ width: 10, height: 10, background: '#ff6a00', color: '#fff', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 6, fontWeight: 'bold' }}>HM</div>
        )}
        <Text strong style={{ fontSize: 7, color: '#333' }}>
          {config.headerTitle || '鹤梦云生态平台'}
        </Text>
      </div>

      {/* Body: sidebar + content */}
      <div style={{ flex: 1, display: 'flex' }}>
        {/* Sidebar */}
        <div style={{ width: 40, background: '#fff', borderRight: '1px solid #f0f0f0', padding: '4px 0' }}>
          {PREVIEW_MENU_ITEMS.map((item, i) => (
            <div key={i} style={{ padding: '2px 4px', display: 'flex', alignItems: 'center', gap: 3 }}>
              <div style={{ fontSize: 6, color: '#999' }}>{item.icon}</div>
              <Text style={{ fontSize: 5, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.label.length > 6 ? item.label.slice(0, 6) + '..' : item.label}
              </Text>
            </div>
          ))}
        </div>
        {/* Content area */}
        <div style={{ flex: 1, background: '#fff', margin: 4, borderRadius: 4 }} />
      </div>
    </div>
  </div>
);

// ══════════════════════════════════════════════
//  主组件：品牌设置
// ══════════════════════════════════════════════
const BrandSetting = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 品牌配置数据
  const [config, setConfig] = useState(MOCK_BRAND_CONFIG);

  // 预览防抖后的配置
  const [previewConfig, setPreviewConfig] = useState(MOCK_BRAND_CONFIG);

  // TODO: 联调时替换为接口数据
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setConfig(MOCK_BRAND_CONFIG);
      setPreviewConfig(MOCK_BRAND_CONFIG);
      setLoading(false);
    }, 300);
  }, []);

  // 防抖预览
  const debounceTimer = React.useRef(null);
  const updatePreview = useCallback((newConfig) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPreviewConfig(prev => ({ ...prev, ...newConfig }));
    }, 500);
  }, []);

  // 表单值变更
  const handleValuesChange = (changedValues) => {
    const newConfig = { ...config, ...changedValues };
    setConfig(newConfig);
    updatePreview(changedValues);
  };

  // 保存
  const handleSave = async () => {
    setSaving(true);
    try {
      // TODO: 联调时替换为实际接口
      await new Promise(resolve => setTimeout(resolve, 800));
      console.log('保存的品牌配置:', config);
      setPreviewConfig(config);
      message.success('保存成功');
    } catch {
      message.error('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  // 图片上传
  const handleUpload = (field) => {
    return {
      accept: field === 'faviconUrl' ? '.ico,.png' : '.png,.jpg,.jpeg',
      maxCount: 1,
      showUploadList: false,
      beforeUpload: (file) => {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/x-icon', 'image/vnd.microsoft.icon'];
        if (!allowedTypes.includes(file.type)) {
          message.error('仅支持 PNG、JPG、ICO 格式');
          return Upload.LIST_IGNORE;
        }
        const maxSizeMap = { faviconUrl: 500, loginLogoUrl: 500, loginBannerUrl: 2048, headerLogoUrl: 500 };
        const maxSize = maxSizeMap[field] || 500;
        if (file.size > maxSize * 1024) {
          message.error(`图片大小不能超过 ${maxSize >= 1024 ? maxSize / 1024 + 'MB' : maxSize + 'KB'}`);
          return Upload.LIST_IGNORE;
        }
        const url = URL.createObjectURL(file);
        form.setFieldsValue({ [field]: url });
        handleValuesChange({ [field]: url });
        return false;
      },
    };
  };

  // 复制品牌链接
  const handleCopyLink = () => {
    const link = `https://平台域名/login?brand=${config.brandCode}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        message.success('链接已复制到剪贴板');
      }).catch(() => {
        message.error('复制失败，请手动复制');
      });
    } else {
      // 兜底：使用 execCommand（兼容非 HTTPS 环境）
      const textarea = document.createElement('textarea');
      textarea.value = link;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      copied ? message.success('链接已复制到剪贴板') : message.error('复制失败，请手动复制');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" tip="加载品牌配置..." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 112px)', gap: 0 }}>
      {/* ═══ 左侧：配置表单 ═══ */}
      <div style={{ flex: 3, overflow: 'auto', padding: 24 }}>
        <Title level={4} style={{ marginBottom: 20 }}>品牌设置</Title>

        <Form
          form={form}
          layout="vertical"
          initialValues={config}
          onValuesChange={handleValuesChange}
          style={{ maxWidth: 720 }}
        >
          {/* 通用设置 */}
          <Card title="通用设置" bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Form.Item label="浏览器标签标题" name="pageTitle">
              <Input placeholder="鹤梦云生态平台" maxLength={30} showCount />
            </Form.Item>
            <Form.Item label="浏览器图标（favicon）" name="faviconUrl"
              extra="建议 32×32px，支持 .ico / .png"
            >
              <Upload {...handleUpload('faviconUrl')}>
                <Button icon={<UploadOutlined />}>上传 favicon</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="启用品牌" name="enabled" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="停用" />
            </Form.Item>
            {!config.enabled && (
              <div style={{ padding: '8px 12px', background: '#fffbe6', borderRadius: 6, border: '1px solid #ffe58f' }}>
                <Text type="warning" style={{ fontSize: 13 }}>当前品牌已停用，下级企业将看到平台默认品牌</Text>
              </div>
            )}
          </Card>

          {/* 登录页设置 */}
          <Card title="登录页设置" bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Form.Item label="登录页 Logo" name="loginLogoUrl" extra="建议 PNG 透明底">
              <Upload {...handleUpload('loginLogoUrl')}>
                <Button icon={<UploadOutlined />}>上传 Logo</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="登录页品牌名称" name="loginBrandName">
              <Input placeholder="鹤梦云生态管理平台" maxLength={20} showCount />
            </Form.Item>
            <Divider style={{ margin: '8px 0 16px' }} />
            <Form.Item label="Banner 背景图" name="loginBannerUrl" extra="上传后替代左侧功能卡片区，建议 800×1200px，最大 2MB">
              <Upload {...handleUpload('loginBannerUrl')}>
                <Button icon={<UploadOutlined />}>上传 Banner</Button>
              </Upload>
            </Form.Item>
            <Divider style={{ margin: '8px 0 16px' }} />
            <Form.Item label="Hero 大标题第一行" name="loginHeroTitle">
              <Input placeholder="为行业领导者打造的" maxLength={30} showCount />
            </Form.Item>
            <Form.Item label="Hero 大标题高亮行" name="loginHeroHighlight">
              <Input placeholder="一站式物联网生态系统" maxLength={20} showCount />
            </Form.Item>
            <Form.Item label="Hero 副标题" name="loginHeroSubtitle">
              <Input placeholder="赋能制造商、解决方案提供商和分销商" maxLength={50} showCount />
            </Form.Item>
          </Card>

          {/* 主界面设置 */}
          <Card title="主界面设置" bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Form.Item label="顶部 Header Logo" name="headerLogoUrl" extra="建议 48×48px，显示在顶部导航栏">
              <Upload {...handleUpload('headerLogoUrl')}>
                <Button icon={<UploadOutlined />}>上传 Logo</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="顶部 Header 标题" name="headerTitle">
              <Input placeholder="鹤梦云生态平台" maxLength={20} showCount />
            </Form.Item>
          </Card>

          {/* 品牌链接 + 操作栏 */}
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>品牌登录链接</Text>
              <Input
                value={`https://平台域名/login?brand=${config.brandCode}`}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: 13, marginBottom: 4 }}
                suffix={
                  <Button type="link" size="small" icon={<CopyOutlined />} onClick={handleCopyLink}>复制</Button>
                }
              />
              {!config.enabled && (
                <Text type="warning" style={{ fontSize: 12 }}>品牌未启用，链接暂不生效</Text>
              )}
            </div>
            <Space>
              <Button type="primary" onClick={handleSave} loading={saving} icon={<PlusOutlined />}>保存</Button>
              <Button onClick={() => { form.resetFields(); setConfig(MOCK_BRAND_CONFIG); }}>取消</Button>
            </Space>
          </Card>
        </Form>
      </div>

      {/* ═══ 右侧：预览区 ═══ */}
      <div style={{ flex: 2, background: '#fafafa', borderLeft: '1px solid #f0f0f0', overflow: 'auto', padding: 24, minWidth: 360 }}>
        <Title level={5} style={{ marginBottom: 16 }}>品牌效果预览</Title>
        <Tabs
          defaultActiveKey="login"
          items={[
            {
              key: 'login',
              label: '登录页预览',
              children: (
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                    {config.enabled ? '预览效果（防抖 500ms 更新）' : '品牌已停用，将展示默认品牌'}
                  </Text>
                  <LoginPreview config={config.enabled ? previewConfig : MOCK_BRAND_CONFIG} />
                </div>
              ),
            },
            {
              key: 'main',
              label: '主界面预览',
              children: (
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>预览 Header + 侧栏菜单效果</Text>
                  <MainPreview config={config.enabled ? previewConfig : MOCK_BRAND_CONFIG} />
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default BrandSetting;
